import { Downloader } from "./downloader.js";
import { TruyenFullAdapter } from "../adapters/truyenfull/index.js";
import { MeTruyenChuComVnAdapter } from "../adapters/metruyenchu/index.js";
import { WikicvAdapter } from "../adapters/wikicv/index.js";
import { TangThuVienAdapter } from "../adapters/tangthuvien/index.js";
import type { SiteAdapter } from "../adapters/base.js";
import type { HttpClient } from "../http/types.js";
import type { Logger } from "../utils/logger.js";
import type { TransportConfig, TransportMode, RateLimitOptions, RetryOptions } from "../data-model/options.js";
import type { LogLevel } from "../utils/logger.js";

/** Fresh instances of every built-in site adapter. */
export function builtinAdapters(): SiteAdapter[] {
  return [
    new TruyenFullAdapter(),
    new MeTruyenChuComVnAdapter(),
    new WikicvAdapter(),
    new TangThuVienAdapter(),
  ];
}

/** Options for {@link createDownloader}. All fields are optional. */
export interface CreateDownloaderOptions {
  /** Transport mode/config. Default: `{ mode: "auto" }` (headless). */
  readonly transport?: TransportConfig | TransportMode;
  /** Replace the entire transport stack with a custom {@link HttpClient}. */
  readonly http?: HttpClient;
  readonly logger?: Logger;
  /** Convenience: build a pino logger at this level when no `logger` is given. */
  readonly logLevel?: LogLevel;
  /** Adapters to register. Defaults to every built-in adapter. */
  readonly adapters?: readonly SiteAdapter[];
  readonly rateLimit?: RateLimitOptions;
  readonly retry?: RetryOptions;
}

/**
 * Build a Downloader wired with the built-in site adapters. Unlike the shared
 * `downloader` singleton, this lets you set the transport (e.g. a headed
 * browser for a manual Cloudflare solve), a custom HttpClient, or a logger.
 * Call `dispose()` when done — the instance may hold a browser pool.
 */
export function createDownloader(opts: CreateDownloaderOptions = {}): Downloader {
  return new Downloader({
    adapters: opts.adapters ?? builtinAdapters(),
    ...(opts.transport !== undefined ? { transport: opts.transport } : {}),
    ...(opts.http !== undefined ? { http: opts.http } : {}),
    ...(opts.logger !== undefined ? { logger: opts.logger } : {}),
    ...(opts.logLevel !== undefined ? { logLevel: opts.logLevel } : {}),
    ...(opts.rateLimit !== undefined ? { rateLimit: opts.rateLimit } : {}),
    ...(opts.retry !== undefined ? { retry: opts.retry } : {}),
  });
}

/**
 * Shared, process-wide downloader: every built-in adapter, default (auto)
 * transport. Holds a BrowserPool that is never disposed automatically — fine
 * for short-lived CLIs. Callers needing deterministic teardown (tests,
 * long-lived servers) should build their own via `createDownloader()` and call
 * `dispose()` when done.
 */
export const downloader = createDownloader();
